/**
 * Admin org management store for the Email Marketing SaaS app.
 *
 * Super-admins can switch between orgs. The selected org_id is persisted
 * in localStorage so a page refresh keeps the selection. When a non-null
 * selectedOrgId is present, the API client injects X-Tenant-Id on every
 * request, overriding the hostname-resolved org on the backend.
 *
 * 2026-05-21: `adminLevel` field retired alongside the admin_level JWT
 * field. It was never wired into a live setter (no auth-context hook
 * called setAdminLevel), so removing it is a no-op for runtime behavior.
 * Org switching is now strictly super-admin-only.
 *
 * Import cycle resolution
 * -----------------------
 * Previously: admin.ts -> client.ts -> admin.ts (circular).
 * Fix: OrgSummary lives in types/admin.ts (no deps). client.ts exposes
 * registerOrgIdProvider() instead of importing the store. This file calls
 * registerOrgIdProvider() once the store is created, wiring the getter
 * without creating a cycle.
 *
 * Dependency graph after fix:
 *   types/admin.ts  (pure types, no runtime deps)
 *   api/client.ts   (no import of admin module)
 *   lib/admin.ts -> api/client.ts (one-way, no cycle)
 *                -> types/admin.ts (pure type import)
 *
 * @module lib/admin
 */

import { apiClient } from "@plerity/dashboard-shared/hooks";
import { createAppStore } from "@plerity/dashboard-shared/stores";
import { persist } from "zustand/middleware";
import { client, registerOrgIdProvider } from "../api/client";
import { asResponseGuard, ItemsEnvelopeGuard } from "../api/schemas";
import type { OrgSummary } from "../types/admin";

export type { OrgSummary };

interface AdminState {
  /** Admin override org_id -- null means use hostname-resolved org */
  selectedOrgId: number | null;
  /** Available orgs loaded from the admin API */
  organizations: OrgSummary[];
  /** Whether the current session belongs to a super-admin */
  isSuperAdmin: boolean;
  loading: boolean;
  error: string | null;

  setSelectedOrgId: (orgId: number | null) => void;
  fetchOrganizations: () => Promise<void>;
  setIsSuperAdmin: (val: boolean) => void;
  reset: () => void;
}

export const useAdminStore = createAppStore<AdminState>(
  "email-admin",
  persist(
    (set) => ({
      selectedOrgId: null,
      organizations: [],
      isSuperAdmin: false,
      loading: false,
      error: null,

      setSelectedOrgId: (orgId) => set({ selectedOrgId: orgId }),

      fetchOrganizations: async () => {
        set({ loading: true, error: null });
        try {
          const res = await client.get<{
            success: boolean;
            data: { items: OrgSummary[]; total: number };
          }>("/api/admin/organizations");
          // Layer-2 fetch-boundary guard: a lenient `{ items }` envelope check
          // catches gross shape drift (null body, non-array `items`) before the
          // org switcher renders. See
          // agents/plans/response_shape_fleet_rollout_plan_2026-06-09.md.
          const data = asResponseGuard<{ items: OrgSummary[]; total: number }>(ItemsEnvelopeGuard).parse(res.data.data);
          set({ organizations: data.items ?? [], loading: false });
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to load organizations";
          set({ loading: false, error: message });
          // Re-throw so TenantContextLoader can detect non-super-admin (403)
          // and set isSuperAdmin=false correctly.
          throw err;
        }
      },

      setIsSuperAdmin: (val) => set({ isSuperAdmin: val }),

      reset: () =>
        set({
          selectedOrgId: null,
          organizations: [],
          isSuperAdmin: false,
          loading: false,
          error: null,
        }),
    }),
    {
      name: "email-admin-org",
      /** Persist org selection + super-admin flag so auth expiry doesn't hide the dropdown */
      partialize: (state) => ({
        selectedOrgId: state.selectedOrgId,
        isSuperAdmin: state.isSuperAdmin,
      }),
    }
  )
);

/**
 * Wire the X-Tenant-Id provider into the API client.
 *
 * client.ts cannot import useAdminStore directly (would recreate the cycle),
 * so it exposes registerOrgIdProvider() for late binding. We call it here,
 * after the store is defined, using a getter closure. The interceptor in
 * client.ts reads this getter at request time -- always after module
 * initialisation is complete.
 */
registerOrgIdProvider(() => useAdminStore.getState().selectedOrgId);

/**
 * Wire the X-Tenant-Id provider into the SHARED dashboard `apiClient`.
 *
 * The dashboard-shared hooks (useApiQuery, usePaginatedQuery) use their own
 * internal `apiClient`, distinct from this app's local `client`. Without this
 * interceptor, switching orgs in the dropdown only affects mutations (which use
 * the local `client` wired via registerOrgIdProvider above) — shared-hook
 * queries continue to fetch data for the hostname-resolved org.
 *
 * Registering the same X-Tenant-Id interceptor here — admin.ts being the
 * canonical owner of `selectedOrgId` — makes ALL requests (queries AND
 * mutations) carry X-Tenant-Id when a super-admin has selected a specific org.
 * `main.tsx` invokes this once at app startup so the interceptor is installed
 * before the first shared-hook query fires. The callback reads the store at
 * request time, after module initialisation, so there is no timing issue.
 */
export function registerSharedApiClientOrgInterceptor(): void {
  apiClient.interceptors.request.use((config) => {
    const selectedOrgId = useAdminStore.getState().selectedOrgId;
    if (selectedOrgId !== null) {
      config.headers.set("X-Tenant-Id", String(selectedOrgId));
    }
    return config;
  });
}
